import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import {
  getNewsletterById,
  updateNewsletter,
} from "../../../project/services/NewsletterService";
import { useFormik } from "formik";
import Header from "../inc/Header";
import Navigation from "../inc/Navigation";
import { ToastContainer } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { listLanguage } from "../../request/Request";
import { useEnvoiAbonnes } from "../../hooks/useEnvoiAbonnes";
import { NEWSLETTER_ENVOI_API } from "../../request/NewsletterRequest";
import WorkflowStepper from "../inc/WorkflowStepper";
import EnvoiAbonnesModal from "../inc/EnvoiAbonnesModal";
import { useVersionsNewsletter } from "../../hooks/useVersionsNewsletter";
import useAlerteSortie, { confirmerSortie } from "../../hooks/useAlerteSortie";
import { BoutonEnregistrer } from "../inc/Loading";
import Failed, { Warning } from "../inc/Failed";

const ENVOI_MODAL_ID = "confirmationEnvoiNewsletter";

export default function UpdateNewsletter() {
  const [date, setDate] = useState("");
  const [titre, setTitre] = useState("");
  const [numero, setNumero] = useState("");
  const [cover, setCover] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [content, setContent] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasModifications, setHasModifications] = useState(false);
  useAlerteSortie(hasModifications && !loading);
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  useEffect(() => {
    listLanguage().then((reponse) => {
      if (reponse?.status === 200) setLanguages(reponse.data);
    });
  }, []);
  const envoi = useEnvoiAbonnes(NEWSLETTER_ENVOI_API);
  // Numéro chargé (et non saisi) : les onglets ne bougent pas pendant qu'on tape un nouveau numéro.
  const [numeroCharge, setNumeroCharge] = useState("");
  const versions = useVersionsNewsletter(numeroCharge, languages);

  // Récupérer l'ID depuis l'URL (ex: /update/:id)
  useEffect(() => {
    if (id) {
      // Changement d'onglet = autre id : on repart d'un fichier de couverture vide.
      setCover(null);
      setPreviewUrl(null);
      setMessage("");
      setHasModifications(false);
      getNewsletterById(id).then((data) => {
        setTitre(data.titre || "");
        setNumero(data.numero || "");
        setNumeroCharge(data.numero || "");
        setDate(data.date_sortie ? data.date_sortie.slice(0, 10) : "");
        setContent(data.content || "");
        setCoverUrl(data.cover || null);
        setWorkflow({ isVerified: data.verified, isSent: data.send });
        setSelectedLanguage(data.language?.idlanguage ?? null);
      });
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const updatedNewsletter = {
        titre,
        numero: parseInt(numero, 10),
        date_sortie: date,
        content,
        cover: "", // sera remplacé côté backend si image
        isDeleted: 0,
        language: { idlanguage: selectedLanguage },
      };
      // Si cover modifié, tu peux adapter le service pour gérer l'upload

      const response = await updateNewsletter(id, updatedNewsletter, cover);
      if (response && response.id_newsletter) {
        setHasModifications(false);

        // Redirection avec state success
        navigate(process.env.REACT_APP_ADMIN_URL + "/list-Newsletter", {
          state: { success: true },
        });
      } else {
        setMessage("Erreur lors de l'enregistrement.");
      }
    } catch (err) {
      setMessage("Erreur lors de la modification.");
    }
    setLoading(false);
  };

  // Mises à jour locales : recharger la newsletter écraserait les modifications non enregistrées du formulaire.
  const verifier = async () => {
    if (await envoi.verify(id)) setWorkflow({ ...workflow, isVerified: true });
  };

  const onSent = ({ isComplete }) => {
    if (isComplete) navigate(process.env.REACT_APP_ADMIN_URL + "/list-Newsletter");
    else setWorkflow({ ...workflow, isSent: true });
  };

  return (
    <>
      <Header />
      <Navigation />
      <ToastContainer />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Newsletter</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to={process.env.REACT_APP_ADMIN_URL + "/dashboard"}>Accueil</Link></li>
              <li className="breadcrumb-item"><Link to={process.env.REACT_APP_ADMIN_URL + "/list-Newsletter"}>Newsletters</Link></li>
              <li className="breadcrumb-item active">Modifier</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="row">
            <div className="card">
              <div className="card-body">
                <h2 className="card-title">Modification</h2>
                <ul className="nav nav-tabs nav-tabs-axian-rouge mb-3">
                  {languages.map((lang) => {
                    const code = lang.code?.toUpperCase();
                    const idVersion = versions[lang.idlanguage];
                    const isActive = idVersion !== undefined && String(idVersion) === String(id);
                    return (
                      <li className="nav-item" key={lang.idlanguage}>
                        <button type="button" className={"nav-link" + (isActive ? " active" : "")}
                          aria-current={isActive ? "page" : undefined} disabled={idVersion === undefined}
                          title={idVersion === undefined ? `Version ${code} manquante` : `Version ${code}`}
                          onClick={() => !isActive && confirmerSortie(hasModifications) && navigate(process.env.REACT_APP_ADMIN_URL + "/update-NewsLetter/" + idVersion)}>
                          {code}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {workflow && (
                  <WorkflowStepper isVerified={workflow.isVerified} isSent={workflow.isSent}
                    onVerify={verifier} onOpenEnvoi={() => envoi.openEnvoi(id)} modalId={ENVOI_MODAL_ID} />
                )}
                <form
                  className="formulaire form-actualite"
                  onSubmit={handleSubmit}
                  onChange={() => setHasModifications(true)}
                  onReset={() => {
                    setCover(null);
                    setPreviewUrl(null);
                  }}
                >
                  {/* Numéro */}
                  <div className="row mb-3">
                    <label
                      htmlFor="inputNumero"
                      className="col-sm-2 col-form-label"
                    >
                      Numéro
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="number"
                        className="form-control"
                        id="numero"
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                        required
                        min={1}
                      />
                    </div>
                  </div>
                  {/* Langue */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Langue</label>
                    <div className="col-sm-10">
                      {languages.map((lang) => (
                        <div className="form-check form-check-inline" key={lang.idlanguage}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="language"
                            id={`lang-${lang.idlanguage}`}
                            value={lang.idlanguage}
                            checked={selectedLanguage === lang.idlanguage}
                            onChange={() => setSelectedLanguage(lang.idlanguage)}
                            required
                          />
                          <label className="form-check-label" htmlFor={`lang-${lang.idlanguage}`}>
                            {lang.code}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Date */}
                  <div className="row mb-3">
                    <label
                      htmlFor="inputDate"
                      className="col-sm-2 col-form-label"
                    >
                      Date
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="date"
                        className="form-control"
                        id="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  {/* Titre */}
                  <div className="row mb-3">
                    <label
                      htmlFor="inputText"
                      className="col-sm-2 col-form-label"
                    >
                      Titre
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="titre"
                        value={titre}
                        onChange={(e) => setTitre(e.target.value)}
                      />
                    </div>
                  </div>
                  {/* Cover */}
                  <div className="row mb-3">
                    <label
                      htmlFor="inputFile"
                      className="col-sm-2 col-form-label"
                    >
                      Image de couverture{" "}
                      <span className="max-size-indication">
                        ( &le; {process.env.REACT_APP_MAX_IMAGE_SIZE} Mo)
                      </span>
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="file"
                        className="form-control"
                        id="cover"
                        name="cover"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          setCover(file);
                          setPreviewUrl(
                            file ? URL.createObjectURL(file) : null,
                          );
                        }}
                      />
                      {cover && cover.size * 0.000001 > process.env.REACT_APP_MAX_IMAGE_SIZE && (
                        <div className="mt-2"><Warning message="Taille de l'image de couverture trop grande" /></div>
                      )}
                      {coverUrl && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Fichier actuel :{" "}
                            <strong>{coverUrl.split("/").pop()}</strong>
                          </small>
                          {!previewUrl && (
                            <div className="mt-1">
                              <img
                                src={coverUrl}
                                alt="Aperçu cover"
                                style={{
                                  width: "120px",
                                  height: "auto",
                                  borderRadius: "4px",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {previewUrl && (
                        <div className="mt-2">
                          <small style={{ color: "#198754" }}>
                            Nouveau fichier sélectionné
                          </small>
                          <div className="mt-1">
                            <img
                              src={previewUrl}
                              alt="Aperçu nouveau"
                              style={{
                                width: "120px",
                                height: "auto",
                                borderRadius: "4px",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="row mb-3">
                    <label
                      htmlFor="content"
                      className="col-sm-2 col-form-label"
                    >
                      Texte
                    </label>
                    <div className="col-sm-10">
                      <div id="ckeditor-wrapper">
                        <Editor
                          apiKey={process.env.REACT_APP_API_KEY_TINY_MCE}
                          value={content}
                          onEditorChange={(newValue, editor) => {
                            setContent(newValue);
                            if (editor.isDirty()) setHasModifications(true);
                          }}
                          init={{
                            height: 500,
                            menubar:
                              "favs view file edit insert format tools table",
                            plugins: [
                              "fullscreen",
                              "advlist",
                              "autolink",
                              "lists",
                              "link",
                              "image",
                              "charmap",
                              "print",
                              "preview",
                              "anchor",
                              "searchreplace",
                              "visualblocks",
                              "code",
                              "insertdatetime",
                              "media",
                              "table",
                              "paste",
                              "wordcount",
                              "template",
                            ],
                            style_formats: [
                              {
                                title: "Ombre gris",
                                block: "div",
                                classes: "shadow-gray",
                                wrapper: true,
                              },
                              {
                                title: "paragraph",
                                block: "div",
                                classes: "clear-paragraph",
                                wrapper: true,
                              },
                            ],
                            templates: [
                              {
                                title: "Deux colonnes (flex)",
                                description:
                                  "Insérer une structure à deux colonnes",
                                content:
                                  '<div className="two-columns"><div><p>Colonne 1...</p></div><div><p>Colonne 2...</p></div></div>',
                              },
                            ],
                            toolbar:
                              "template | fullscreen undo redo | formatselect | " +
                              "bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat",
                            content_style: `
                                                            body{font-family:AdriaGrotesk-Thin;}
                                                            .two-columns{display:flex; gap:50px;}
                                                            .two-columns > div{flex:1;}
                                                            .shadow-gray{  background-color: #f7f7f7;
                                                                padding: 20px 30px;
                                                                border-radius: 34px;
                                                                box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                                                                max-width: 1240px;   }
                                                            `,
                            font_family_formats:
                              "Roboto Medium=Roboto-Medium;Roboto Light=Roboto-Light;Roboto Thin=Roboto-Thin;Roboto Black=Roboto-Black;Roboto Regular=Roboto-Regular;Roboto Bold=Roboto-Bold;",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {message && (
                    <div className="row mb-3">
                      <label className="col-sm-2 col-form-label"></label>
                      <div className="col-sm-10"><Failed message={message} /></div>
                    </div>
                  )}
                  <div className="form-actions">
                    <BoutonEnregistrer isSaving={loading} />
                    <Link to={process.env.REACT_APP_ADMIN_URL + "/list-Newsletter"} className="btn btn-outline-secondary"
                      onClick={(e) => { if (!confirmerSortie(hasModifications)) e.preventDefault(); }}>Annuler</Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <EnvoiAbonnesModal modalId={ENVOI_MODAL_ID} id={id} envoi={envoi} onSent={onSent}
        envoiLabel="Cette newsletter sera envoyée à" />
    </>
  );
}
